import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { pruneTimelineExclude } from '@/features/schema/format'
import type { RelationFormState } from '@/features/schema/types'
import type { ValidationFinding } from '@/services/api/schemas'
import { ConventionsEditor } from './ConventionsEditor'
import { DialogValidationFindings } from './DialogValidationFindings'
import { PropertyEditor } from './PropertyEditor'

export interface RelationFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  isEditing: boolean
  form: RelationFormState
  onFormChange: (form: RelationFormState) => void
  saving: boolean
  onSave: () => void
  findings?: ValidationFinding[] | null
}

/**
 * Controlled create/edit dialog for a relation type. Pure — emits the next form
 * value and a save intent; the container owns persistence.
 */
export function RelationFormDialog({
  open,
  onOpenChange,
  isEditing,
  form,
  onFormChange,
  saving,
  onSave,
  findings,
}: RelationFormDialogProps) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-lg max-h-[85dvh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-foreground">
            {t(`schemaAgent.editor.relationDialog.${isEditing ? 'titleEdit' : 'titleNew'}`)}
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('schemaAgent.editor.relationDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 overflow-y-auto flex-1 min-h-0 pr-1">
          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">{t('schemaAgent.editor.label')}</Label>
            <Input
              value={form.label}
              onChange={e =>
                onFormChange({
                  ...form,
                  label: e.target.value.toUpperCase().replace(/\s+/g, '_'),
                })
              }
              placeholder={t('schemaAgent.editor.relationDialog.labelPlaceholder')}
              className="bg-muted border-border font-mono"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-sm text-muted-foreground">
              {t('schemaAgent.editor.description')}
            </Label>
            <Textarea
              value={form.description}
              onChange={e => onFormChange({ ...form, description: e.target.value })}
              placeholder={t('schemaAgent.editor.relationDialog.descriptionPlaceholder')}
              className="bg-muted border-border h-[120px] resize-none overflow-y-auto"
            />
          </div>

          <PropertyEditor
            properties={form.properties}
            onChange={props => {
              onFormChange({
                ...form,
                properties: props,
                timeline_exclude: pruneTimelineExclude(form.timeline_exclude, props),
              })
            }}
          />

          <ConventionsEditor
            kind="relation"
            properties={form.properties}
            timelineExclude={form.timeline_exclude}
            onTimelineExcludeChange={v => onFormChange({ ...form, timeline_exclude: v })}
          />
        </div>

        <DialogValidationFindings findings={findings} />

        <DialogFooter className="shrink-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border"
            disabled={saving}
          >
            {t('common.cancel')}
          </Button>
          <Button onClick={onSave} className="bg-warning hover:bg-warning/90" disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isEditing
              ? t('schemaAgent.editor.saveChanges')
              : t('schemaAgent.editor.relationDialog.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
